/**
 * PublicItineraryPage — customer-facing branded travel plan (/r/:token) (Fase K3).
 * No auth; tenant + booking resolved from the token. Mobile-first. STRICT data
 * minimization: no passengers, no customer contact, no prices; driver first name
 * only; registration only when the tenant enables it. Friendly states for
 * expired/revoked/not-yet-active; renders even when ferry times are unavailable.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Loader2, Bus, User, Phone, Ship, Plane, Clock, Download,
  ArrowRight, ArrowLeftRight, Navigation,
} from 'lucide-react';
import PublicTrackingMap from '@/components/public/PublicTrackingMap';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n/config';
import { usePublicLanguage } from '@/components/public/usePublicLanguage';
import PublicLanguageToggle from '@/components/public/PublicLanguageToggle';

const API = process.env.REACT_APP_BACKEND_URL;

class MapErrorBoundary extends React.Component<{ children: React.ReactNode }, { failed: boolean }> {
  constructor(props: any) { super(props); this.state = { failed: false }; }
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch() { /* contained — never crash the itinerary page */ }
  render() {
    if (this.state.failed) {
      return (
        <div className="w-full h-full flex items-center justify-center bg-slate-50 text-sm text-slate-400 px-4 text-center" data-testid="itinerary-live-map-error">
          {i18n.t('publicItineraryPage.kartetKunneIkkeLastesAkkurat', 'Kartet kunne ikke lastes akkurat nå.')}
        </div>
      );
    }
    return this.props.children as any;
  }
}

const fmtClock = (iso?: string | null, lang = 'no') => {
  if (!iso) return null;
  try { return new Date(iso).toLocaleTimeString(lang === 'en' ? 'en-GB' : 'nb-NO', { hour: '2-digit', minute: '2-digit' }); }
  catch { return null; }
};

const Brand = ({ company, toggle }: { company: any; toggle?: React.ReactNode }) => {
  const c = company || {};
  return (
    <div className="text-center mb-6 relative" data-testid="itinerary-brand">
      {toggle && <div className="absolute right-0 top-0">{toggle}</div>}
      {c.logo && <img src={c.logo} alt={c.name || ''} className="h-12 mx-auto mb-2 object-contain" />}
      {c.name && <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider">{c.name}</p>}
    </div>
  );
};

const StateCard = ({ company, title, message, toggle }: any) => (
  <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
    <div className="w-full max-w-md bg-white rounded-2xl shadow-lg p-8 text-center" data-testid="itinerary-state-card">
      <Brand company={company} toggle={toggle} />
      <h1 className="text-lg font-bold text-slate-800 mb-1">{title}</h1>
      <p className="text-sm text-slate-500">{message}</p>
    </div>
  </div>
);

const kindIcon = (kind: string) => (kind === 'ferje' ? Ship : kind === 'fly' ? Plane : Clock);

export default function PublicItineraryPage() {
  const { t: tt } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<any>(null);
  const { lang, setPublicLanguage } = usePublicLanguage(data?.preferred_language);
  const toggle = <PublicLanguageToggle lang={lang} onChange={setPublicLanguage} />;
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);
  const [liveByLeg, setLiveByLeg] = useState<Record<number, any>>({});
  const liveTimer = useRef<any>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/api/public/itinerary/${token}`);
        if (!res.ok) throw new Error('bad');
        const j = await res.json();
        if (!cancelled) setData(j);
      } catch {
        if (!cancelled) setErr(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // Live position/status poll — authorized by the itinerary token itself.
  useEffect(() => {
    if (!data || data.state !== 'active') return;
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await fetch(`${API}/api/public/itinerary/${token}/live`);
        if (res.ok) {
          const j = await res.json();
          if (!cancelled && j.state === 'active') {
            const m: Record<number, any> = {};
            (j.legs || []).forEach((l: any) => { m[l.leg_index] = l; });
            setLiveByLeg(m);
          }
        }
      } catch { /* keep last */ }
      if (cancelled) return;
      const hidden = document.visibilityState === 'hidden';
      liveTimer.current = setTimeout(poll, hidden ? 60000 : 15000);
    };
    poll();
    const onVis = () => { if (document.visibilityState === 'visible') { clearTimeout(liveTimer.current); poll(); } };
    document.addEventListener('visibilitychange', onVis);
    return () => { cancelled = true; clearTimeout(liveTimer.current); document.removeEventListener('visibilitychange', onVis); };
  }, [data, token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }
  if (err || !data) {
    return <StateCard company={{}} toggle={toggle} title={tt('publicItineraryPage.reiseplanenErIkkeTilgjengelig', 'Reiseplanen er ikke tilgjengelig')}
      message={tt('publicItineraryPage.lenkenErUgyldigEllerUtlopt', 'Lenken er ugyldig eller utløpt. Kontakt operatøren for en ny lenke.')} />;
  }

  const company = data.company || {};
  if (data.state === 'expired') return <StateCard company={company} toggle={toggle} title={tt('publicItineraryPage.reiseplanenErUtlopt', 'Reiseplanen er utløpt')} message={tt('publicItineraryPage.ikkeLengerTilgjengelig', 'Denne reiseplanen er ikke lenger tilgjengelig.')} />;
  if (data.state === 'revoked') return <StateCard company={company} toggle={toggle} title={tt('publicItineraryPage.reiseplanenErTrukketTilbake', 'Reiseplanen er trukket tilbake')} message={tt('publicItineraryPage.kontaktOperatorenForOppdatertLenke', 'Kontakt operatøren for en oppdatert lenke.')} />;
  if (data.state === 'not_yet_active') return <StateCard company={company} toggle={toggle} title={tt('publicItineraryPage.reiseplanenErIkkeAktivEnna', 'Reiseplanen er ikke aktiv ennå')} message={tt('publicItineraryPage.komTilbakeNaermereAvreise', 'Kom tilbake nærmere avreise.')} />;

  const legs: any[] = data.legs || [];
  const pdfUrl = `${API}/api/public/itinerary/${token}/pdf?lang=${lang}`;
  const phone = (data.operator_contact || {}).phone;

  return (
    <div className="min-h-screen bg-slate-50 py-6 px-4" data-testid="itinerary-page">
      <div className="max-w-lg mx-auto">
        <Brand company={company} toggle={toggle} />

        <div className="bg-white rounded-2xl shadow-sm p-5 mb-4">
          <h1 className="text-xl font-bold text-slate-800" data-testid="itinerary-title">{data.journey_title || tt('publicItineraryPage.reiseplan', 'Reiseplan')}</h1>
          {data.customer_name && <p className="text-sm text-slate-500 mt-0.5">{data.customer_name}</p>}
          {data.is_round_trip && (
            <span className="inline-flex items-center gap-1 mt-2 text-xs font-semibold text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded-full">
              <ArrowLeftRight className="w-3 h-3" /> {tt('public.roundTrip', 'Tur/retur')}
            </span>
          )}
        </div>

        {legs.map((leg, i) => {
          const RoleIcon = leg.leg_role === 'retur' ? ArrowLeftRight : ArrowRight;
          const roleTxt = leg.leg_role === 'retur' ? tt('public.legReturn', 'Retur') : leg.leg_role === 'utreise' ? tt('public.legOutbound', 'Utreise') : tt('public.legJourney', 'Reise');
          const legKey = leg.leg_index ?? i;
          const live = liveByLeg[legKey] || leg.live || {};
          const underway = live.status === 'underway' && live.position;
          return (
            <div key={i} className="bg-white rounded-2xl shadow-sm p-5 mb-4" data-testid={`itinerary-leg-${i}`}>
              <div className="flex items-center gap-2 mb-1">
                <RoleIcon className="w-4 h-4 text-teal-600" />
                <span className="text-xs font-bold uppercase tracking-wide text-teal-700">{roleTxt}</span>
                {leg.date && <span className="text-xs text-slate-400 ml-auto">{leg.date}</span>}
              </div>
              <h2 className="text-base font-semibold text-slate-800 mb-3">{leg.from} → {leg.to}</h2>

              {(leg.stops || []).length > 0 && (
                <ol className="relative border-l-2 border-slate-100 pl-4 space-y-2 mb-3">
                  {leg.stops.map((s: any, j: number) => (
                    <li key={j} className="relative" data-testid={`itinerary-stop-${i}-${j}`}>
                      <span className="absolute -left-[21px] top-1 w-2.5 h-2.5 rounded-full bg-teal-500 ring-2 ring-white" />
                      <div className="flex items-baseline gap-2">
                        {s.time && <span className="text-sm font-mono text-slate-500 w-12 shrink-0">{s.time}</span>}
                        <span className="text-sm text-slate-800">{s.location}</span>
                      </div>
                    </li>
                  ))}
                </ol>
              )}

              {(leg.timepoints || []).length > 0 && (
                <div className="space-y-1.5 mb-3">
                  {leg.timepoints.map((t: any, k: number) => {
                    const Icon = kindIcon(t.kind);
                    return (
                      <div key={k} className="flex items-center gap-2 text-sm bg-slate-50 rounded-lg px-3 py-2" data-testid={`itinerary-timepoint-${i}-${k}`}>
                        <Icon className="w-4 h-4 text-sky-600 shrink-0" />
                        <span className="font-mono text-slate-600 w-12 shrink-0">{t.time || '—'}</span>
                        <span className="text-slate-800">{t.label}</span>
                        {t.manual
                          ? <span className="ml-auto text-[10px] font-semibold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded" data-testid={`itinerary-timepoint-manual-${i}-${k}`}>{tt('publicItineraryPage.manueltSatt', 'manuelt satt')}</span>
                          : (t.kind === 'ferje' && <span className="ml-auto text-[10px] font-semibold text-sky-700 bg-sky-100 px-1.5 py-0.5 rounded">{tt('publicItineraryPage.ferjetider', 'ferjetider')}</span>)}
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-slate-600 border-t border-slate-100 pt-3">
                {leg.bus_type && (
                  <span className="flex items-center gap-1.5">
                    <Bus className="w-4 h-4 text-slate-400" />{leg.bus_type}{leg.vehicle_registration ? ` · ${leg.vehicle_registration}` : ''}
                  </span>
                )}
                <span className="flex items-center gap-1.5" data-testid={`itinerary-driver-${i}`}>
                  <User className="w-4 h-4 text-slate-400" />{leg.driver_first_name || tt('publicItineraryPage.sjaforIkkeTildeltEnna', 'Sjåfør ikke tildelt ennå')}
                </span>
              </div>

              {underway && (
                <div className="mt-3" data-testid={`itinerary-live-${i}`}>
                  <div className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-700 bg-emerald-50 px-3 py-1.5 rounded-full" data-testid={`itinerary-live-status-${i}`}>
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                    <Navigation className="w-4 h-4" /> {tt('publicItineraryPage.bussenErUnderveis', 'Bussen er underveis')}
                  </div>
                  <div className="mt-3 rounded-xl overflow-hidden border border-slate-200" data-testid={`itinerary-live-map-${i}`}>
                    <div className="h-72">
                      <MapErrorBoundary>
                        <React.Suspense fallback={<div className="w-full h-full flex items-center justify-center bg-slate-50"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>}>
                          <PublicTrackingMap position={live.position} />
                        </React.Suspense>
                      </MapErrorBoundary>
                    </div>
                    <div className="px-4 py-2 text-xs text-slate-500 bg-white flex items-center gap-3 flex-wrap">
                      {live.driver_first_name && <span className="inline-flex items-center gap-1"><User className="w-3.5 h-3.5" />{live.driver_first_name}</span>}
                      {live.vehicle_type && <span className="inline-flex items-center gap-1"><Bus className="w-3.5 h-3.5" />{live.vehicle_type}</span>}
                      {live.last_updated && <span className="ml-auto">{tt('publicItineraryPage.oppdatert', { defaultValue: 'Oppdatert {{v1}}', v1: fmtClock(live.last_updated, lang) })}</span>}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        <div className="bg-white rounded-2xl shadow-sm p-5 mb-4 flex items-center justify-between gap-3">
          <div className="text-sm text-slate-600 min-w-0">
            {phone
              ? <a href={`tel:${phone}`} className="flex items-center gap-1.5 text-slate-700" data-testid="itinerary-operator-phone"><Phone className="w-4 h-4 text-slate-400 shrink-0" /><span className="truncate">{phone}</span></a>
              : <span className="text-slate-400">{tt('publicItineraryPage.kontaktOperatorenVedSporsmal', 'Kontakt operatøren ved spørsmål')}</span>}
          </div>
          <a href={pdfUrl} target="_blank" rel="noreferrer" data-testid="itinerary-pdf-btn"
             className="inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold px-4 py-2 rounded-xl transition-colors shrink-0">
            <Download className="w-4 h-4" /> {tt('publicItineraryPage.lastNedPdf', 'Last ned PDF')}
          </a>
        </div>
      </div>
    </div>
  );
}
